import { useCallback, useMemo, useState } from 'react';
import { MarketingFilters } from '../models';
import { getCurrentMonth, sanitizeMonthDate } from '../utils';

interface UseMarketingFiltersReturn {
  // Current filter values
  refDate: string;
  countryId: number | null;
  customerOrigins: string[];

  // Computed values
  marketingFilters: MarketingFilters;
  hasActiveFilters: boolean;

  // Actions
  updateFilters: (filters: {
    refDate?: string;
    countryId?: number | null;
    customerOrigins?: string[];
  }) => void;

  // Helpers
  getCurrentMonth: () => string;
}

export const useMarketingFilters = (): UseMarketingFiltersReturn => {
  const currentMonth = getCurrentMonth();

  // Local state management instead of URL params
  const [refDate, setRefDate] = useState(currentMonth);
  const [countryId, setCountryId] = useState<number | null>(null);
  const [customerOrigins, setCustomerOrigins] = useState<string[]>([]);

  // Stable getCurrentMonth function
  const getCurrentMonthCallback = useCallback(() => currentMonth, [currentMonth]);

  // Convert to API filters format
  const marketingFilters: MarketingFilters = useMemo(() => {
    const filters: MarketingFilters = {};

    // Only send date if different from current month
    if (refDate && refDate !== currentMonth) {
      filters.ref_date = refDate;
    }

    // Only send filters that have values
    if (countryId !== null) {
      filters.country_id = countryId;
    }

    if (customerOrigins && customerOrigins.length > 0) {
      filters.customer_origin = customerOrigins;
    }

    return filters;
  }, [refDate, countryId, customerOrigins, currentMonth]);

  // Detect active filters
  const hasActiveFilters = useMemo(() => {
    if (refDate && refDate !== currentMonth) return true;
    if (countryId !== null) return true;
    if (customerOrigins && customerOrigins.length > 0) return true;
    return false;
  }, [refDate, countryId, customerOrigins, currentMonth]);

  // Update filters handler
  const updateFilters = useCallback((filters: {
    refDate?: string;
    countryId?: number | null;
    customerOrigins?: string[];
  }) => {
    // Update local state directly
    if (filters.refDate !== undefined) {
      // Validate and sanitize date before updating
      const sanitizedDate = sanitizeMonthDate(filters.refDate);
      setRefDate(sanitizedDate);
    }

    if (filters.countryId !== undefined) {
      setCountryId(filters.countryId);
    }

    if (filters.customerOrigins !== undefined) {
      setCustomerOrigins(filters.customerOrigins);
    }
  }, []);

  return {
    refDate,
    countryId,
    customerOrigins,
    marketingFilters,
    hasActiveFilters,
    updateFilters,
    getCurrentMonth: getCurrentMonthCallback,
  };
};